import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip,
  Paper,
  Alert,
  Fab,
  Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  LocationOn as LocationIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';

const ShippingLocations = ({ currentUser }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [locations, setLocations] = useState([
    {
      id: 'LOC-001',
      name: 'Main Clinic',
      address: {
        street: '123 Medical Plaza',
        city: 'Los Angeles',
        state: 'CA',
        zipCode: '90210'
      },
      isDefault: true,
      createdDate: '2024-01-15'
    },
    {
      id: 'LOC-002',
      name: 'Downtown Office',
      address: {
        street: '456 Business Ave',
        city: 'Los Angeles',
        state: 'CA',
        zipCode: '90012'
      },
      isDefault: false,
      createdDate: '2024-01-20'
    },
    {
      id: 'LOC-003',
      name: 'Satellite Clinic',
      address: {
        street: '789 Healthcare Blvd',
        city: 'Beverly Hills',
        state: 'CA',
        zipCode: '90210'
      },
      isDefault: false,
      createdDate: '2024-01-25'
    }
  ]);

  const [addLocationOpen, setAddLocationOpen] = useState(false);
  const [editLocationOpen, setEditLocationOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    street: '',
    city: '',
    state: '',
    zipCode: ''
  });

  const handleAddLocation = () => {
    const newLocation = {
      id: `LOC-${Date.now()}`,
      name: formData.name,
      address: {
        street: formData.street,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode
      },
      isDefault: locations.length === 0, // First location is default
      createdDate: new Date().toISOString().split('T')[0]
    };

    setLocations(prev => [...prev, newLocation]);
    setAddLocationOpen(false);
    resetForm();
  };

  const handleEditLocation = () => {
    const updatedLocations = locations.map(loc => 
      loc.id === selectedLocation.id 
        ? {
            ...loc,
            name: formData.name,
            address: {
              street: formData.street,
              city: formData.city,
              state: formData.state,
              zipCode: formData.zipCode
            }
          }
        : loc
    );

    setLocations(updatedLocations);
    setEditLocationOpen(false);
    setSelectedLocation(null);
    resetForm();
  };

  const handleDeleteLocation = (locationId) => {
    const locationToDelete = locations.find(loc => loc.id === locationId);
    
    if (locationToDelete.isDefault && locations.length > 1) {
      // If deleting default, make the first remaining location default
      const updatedLocations = locations
        .filter(loc => loc.id !== locationId)
        .map((loc, index) => index === 0 ? { ...loc, isDefault: true } : loc);
      setLocations(updatedLocations);
    } else {
      setLocations(prev => prev.filter(loc => loc.id !== locationId));
    }
  };

  const handleSetDefault = (locationId) => {
    const updatedLocations = locations.map(loc => ({
      ...loc,
      isDefault: loc.id === locationId
    }));
    setLocations(updatedLocations);
  };

  const openEditDialog = (location) => {
    setSelectedLocation(location);
    setFormData({
      name: location.name,
      street: location.address.street,
      city: location.address.city,
      state: location.address.state,
      zipCode: location.address.zipCode
    });
    setEditLocationOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      street: '',
      city: '',
      state: '',
      zipCode: ''
    });
  };

  const isFormValid = () => {
    return formData.name.trim() && 
           formData.street.trim() && 
           formData.city.trim() && 
           formData.state.trim() && 
           formData.zipCode.trim();
  };

  const StatCard = ({ title, count, color, description, icon }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color }}>
              {count}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
          {React.cloneElement(icon, { sx: { color, fontSize: 32 } })}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4, pt: { xs: 7, md: 3 }, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'flex-start' }, flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Shipping Locations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your delivery addresses for easy order placement
          </Typography>
        </Box>
        <Fab 
          variant="extended" 
          color="primary" 
          onClick={() => setAddLocationOpen(true)}
          sx={{ ml: { md: 2 }, alignSelf: { xs: 'flex-start', md: 'auto' }, display: { xs: 'none', md: 'inline-flex' } }}
        >
          <AddIcon sx={{ mr: 1 }} />
          Add Location
        </Fab>
      </Box>
      {isMobile && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddLocationOpen(true)}
          fullWidth
          sx={{ mb: 3 }}
        >
          Add Location
        </Button>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Locations"
            count={locations.length}
            color="#0d9488"
            description="delivery addresses"
            icon={<LocationIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Default Location"
            count={locations.filter(loc => loc.isDefault).length}
            color="#FFA73C"
            description="primary address"
            icon={<StarIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Recent Additions"
            count={locations.filter(loc => {
              const created = new Date(loc.createdDate);
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
              return created > thirtyDaysAgo;
            }).length}
            color="#28A745"
            description="last 30 days"
            icon={<AddIcon />}
          />
        </Grid>
      </Grid>

      {/* Locations Table / Mobile List */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Your Shipping Locations ({locations.length})
            </Typography>
          </Box>

          {locations.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <LocationIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No shipping locations yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Add your first shipping location to start placing orders
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddLocationOpen(true)}
              >
                Add Location
              </Button>
            </Box>
          ) : (
            isMobile ? (
              <Stack spacing={1.5} sx={{ p: 2 }}>
                {locations.map((location) => (
                  <Paper key={location.id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationIcon sx={{ mr: 1.5, color: '#0d9488' }} />
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {location.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {location.id}
                          </Typography>
                        </Box>
                      </Box>
                      {location.isDefault ? (
                        <Chip icon={<StarIcon />} label="Default" color="success" size="small" />
                      ) : (
                        <Button size="small" variant="outlined" onClick={() => handleSetDefault(location.id)}>
                          Make Default
                        </Button>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {location.address.street}, {location.address.city}, {location.address.state} {location.address.zipCode}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Created: {new Date(location.createdDate).toLocaleDateString()}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="contained" onClick={() => openEditDialog(location)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleDeleteLocation(location.id)}
                        disabled={locations.length === 1}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Location Name</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell align="center">Default</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {locations.map((location) => (
                      <TableRow key={location.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationIcon sx={{ mr: 2, color: '#0d9488' }} />
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {location.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ID: {location.id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {location.address.street}<br />
                            {location.address.city}, {location.address.state} {location.address.zipCode}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {location.isDefault ? (
                            <Chip
                              icon={<StarIcon />}
                              label="Default"
                              color="success"
                              size="small"
                            />
                          ) : (
                            <Tooltip title="Set as Default">
                              <IconButton
                                size="small"
                                onClick={() => handleSetDefault(location.id)}
                              >
                                <StarBorderIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(location.createdDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="Edit Location">
                              <IconButton 
                                size="small" 
                                onClick={() => openEditDialog(location)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Location">
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteLocation(location.id)}
                                color="error"
                                disabled={locations.length === 1}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
        </CardContent>
      </Card>

      {/* Add Location Modal */}
      <Dialog open={addLocationOpen} onClose={() => setAddLocationOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Shipping Location</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Add a new shipping address where you'd like to receive orders.
          </Alert>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Main Office, Downtown Clinic"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.street}
                onChange={(e) => setFormData(prev => ({ ...prev, street: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.zipCode}
                onChange={(e) => setFormData(prev => ({ ...prev, zipCode: e.target.value }))}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddLocationOpen(false); resetForm(); }}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddLocation} 
            variant="contained"
            disabled={!isFormValid()}
            startIcon={<AddIcon />}
          >
            Add Location
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Location Modal */}
      <Dialog open={editLocationOpen} onClose={() => setEditLocationOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Shipping Location</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Update the details for this shipping location.
          </Alert>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.street}
                onChange={(e) => setFormData(prev => ({ ...prev, street: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.zipCode}
                onChange={(e) => setFormData(prev => ({ ...prev, zipCode: e.target.value }))}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditLocationOpen(false); setSelectedLocation(null); resetForm(); }}>
            Cancel
          </Button>
          <Button 
            onClick={handleEditLocation} 
            variant="contained"
            disabled={!isFormValid()}
            startIcon={<EditIcon />}
          >
            Update Location
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShippingLocations;